import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageShell } from '../layout/page-shell.jsx';
import { ProfileTab } from '../settings/profile-tab.jsx';
import { SystemTab } from '../settings/system-tab.jsx';
import { NodesTab } from '../settings/nodes-tab.jsx';
import { RetentionTab } from '../settings/retention-tab.jsx';
import { BridgesTab } from '../settings/bridges-tab.jsx';
import { ScheduleTab } from '../settings/schedule-tab.jsx';
import { LicenseTab } from '../settings/license-tab.jsx';

const VALID_TABS = new Set(['profile', 'system', 'nodes', 'retention', 'bridges', 'schedule', 'license']);

export function SettingsPage({ currentUser, onUserUpdated, onSettingsChanged }) {
  const isAdmin = currentUser?.role === 'admin';
  const location = useLocation();
  const navigate = useNavigate();

  // Allow URL hash to deep-link into a specific tab — e.g. the global
  // license banner sends users to /settings#license.
  const initialFromHash = (location.hash || '').replace(/^#/, '');
  const [tab, setTab] = useState(VALID_TABS.has(initialFromHash) ? initialFromHash : 'profile');

  // React to subsequent hash changes (e.g. clicking the banner while
  // already on /settings).
  useEffect(() => {
    const h = (location.hash || '').replace(/^#/, '');
    if (VALID_TABS.has(h) && h !== tab) setTab(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  // Keep the URL hash in sync when the user clicks a tab so deep-links
  // are bookmarkable.
  function handleTabChange(next) {
    setTab(next);
    navigate({ pathname: '/settings', hash: '#' + next }, { replace: true });
  }

  return (
    <PageShell
      icon="fa-sliders"
      title="Settings"
      subtitle="Profile preferences, system configuration, and KMS license"
    >
      <Tabs
        value={tab}
        onChange={handleTabChange}
        items={[
          { id: 'profile', label: 'Profile', icon: 'fa-user' },
          ...(isAdmin
            ? [
                { id: 'system', label: 'System', icon: 'fa-sliders' },
                // Network-topology config: tier list (formerly hardcoded
                // C4ISR/C2/C1; now operator-managed). New settings related
                // to mesh nodes will accumulate here.
                { id: 'nodes',  label: 'Nodes',  icon: 'fa-network-wired' },
                // Stage-3 N-1 — message + attachment retention windows
                // for chat / data channels. Read by the microservice
                // janitor; defaults shipped on first boot.
                { id: 'retention', label: 'Retention', icon: 'fa-clock-rotate-left' },
                // Operator-managed Tor bridge pool. Distributed to
                // terminals via welcome.meshTorBridges so bridge
                // rotation doesn't require an APK redeploy.
                { id: 'bridges', label: 'Tor bridges', icon: 'fa-bridge' },
                // Round 63 — operator-managed cron schedules. Drives
                // periodic Source fetches + Batch checks against the
                // bridge pool. Extensible to other widgets later.
                { id: 'schedule', label: 'Schedule', icon: 'fa-clock' },
              ]
            : []),
          // License tab is visible to everyone — read-only for non-admins.
          // The Renew button inside the widget is gated separately on role.
          { id: 'license', label: 'License', icon: 'fa-shield-halved' },
        ]}
      />

      <div className="mt-6">
        {tab === 'profile' && (
          <ProfileTab currentUser={currentUser} onUserUpdated={onUserUpdated} />
        )}
        {tab === 'system' && isAdmin && (
          <SystemTab currentUser={currentUser} onSettingsChanged={onSettingsChanged} />
        )}
        {tab === 'nodes' && isAdmin && (
          <NodesTab onSettingsChanged={onSettingsChanged} />
        )}
        {tab === 'retention' && isAdmin && (
          <RetentionTab onSettingsChanged={onSettingsChanged} />
        )}
        {tab === 'bridges' && isAdmin && (
          <BridgesTab currentUser={currentUser} />
        )}
        {tab === 'schedule' && isAdmin && (
          <ScheduleTab />
        )}
        {tab === 'license' && (
          <LicenseTab currentUser={currentUser} />
        )}
      </div>
    </PageShell>
  );
}

function Tabs({ value, onChange, items }) {
  return (
    <div role="tablist" className="flex w-fit items-center gap-1 rounded-2xl border border-line bg-white p-1 shadow-card">
      {items.map((item) => {
        const active = value === item.id;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={
              active
                ? 'flex items-center gap-2 rounded-xl bg-accent-soft px-4 py-2 text-sm font-semibold text-accent'
                : 'flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-mist hover:text-ink'
            }
          >
            <i className={`fa-solid ${item.icon} text-[13px]`} aria-hidden="true" />
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
